import React, { useMemo, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import block from 'bem-cn';

import Spinner from 'components/Spinner/view/desktop';
import useGeneralModel from 'features/reports/hooks/useGeneralModel';
import { generalReportMetrics, providerScheduleMetrics } from 'features/reports/data';

import FilterBlock from './FilterBlock';
import Table from './Table/Table';
import TableProviders from './TableProviders/TableProviders';
import Schedule from './Schedule/Schedule';
import SavedFilters from './SavedFilters/SavedFilters';

import './General.scss';

const b = block('general');

const General = ({
  isMobile = false,
  ProvidersTable = TableProviders,
  ReportFilter = FilterBlock,
  ReportTable = Table,
}) => {
  const locale = useSelector(state => state.locale.locale);
  const { currency } = useSelector(state => state.auth);
  const { actionProcessing, statisticProvider, reportTimeseries, providerTimeseries } = useSelector(
    state => state.reports,
    shallowEqual,
  );

  const model = useGeneralModel();
  const { values } = model;
  const scheduleRef = useRef(null);
  const activeMetric = generalReportMetrics.find(item => item.key === values.activeMetric);
  const handleProviderScheduleOpen = () => {
    model.handleProviderScheduleOpen();
    if (!values.providerMetrics.length) return;

    window.requestAnimationFrame(() => {
      scheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const chartSeries = useMemo(() => {
    if (values.isProviderScheduleOpen) {
      return values.providerMetrics.map(metric => {
        const config = providerScheduleMetrics.find(item => item.key === metric);
        return {
          key: metric,
          label: locale[config?.label] || metric,
          current: providerTimeseries?.[metric]?.current || [],
          previous: providerTimeseries?.[metric]?.previous || [],
          currency: metric !== 'bets_count',
        };
      });
    }

    const timeseries = values.activeMetric === 'activeUsers' ? values.activeUsers.timeseries : reportTimeseries;
    return [{
      key: activeMetric?.key,
      label: locale[activeMetric?.label] || '',
      current: timeseries?.current || [],
      previous: timeseries?.previous || [],
      suffix: activeMetric?.suffix,
      currency: activeMetric?.currency,
    }];
  }, [
    activeMetric,
    locale,
    providerTimeseries,
    reportTimeseries,
    values.isProviderScheduleOpen,
    values.providerMetrics,
    values.activeMetric,
    values.activeUsers.timeseries,
  ]);

  return (
    <div className={b({ isMobile })}>
      <Spinner isLoading={actionProcessing} foolViewport={isMobile} />

      <ReportTable
        activeMetric={values.activeMetric}
        onMetricSelect={model.handleSetActiveMetric}
        onActiveUsersToggle={model.handleActiveUsersToggle}
        withDiff={values.withDiff}
        handleGetDiff={model.handleGetDiff}
        handleSetWithDiff={model.handleSetWithDiff}
        onChangeDiffRange={model.onChangeDiffRange}
        onChangeBeginDiffDate={model.onChangeBeginDiffDate}
        onChangeEndDiffDate={model.onChangeEndDiffDate}
        values={values}
      />

      <ReportFilter
        isMobile={isMobile}
        handleFormSubmit={model.handleFormSubmit}
        handleKeyDown={model.handleKeyDown}
        handleStructureChange={model.handleStructureChange}
        handleSearchChange={model.handleSearchChange}
        handleResultClick={model.handleResultClick}
        onChangeBeginDate={model.onChangeBeginDate}
        onChangeEndDate={model.onChangeEndDate}
        onChangeActiveDay={model.onChangeActiveDay}
        onChangeDiffRange={model.onChangeDiffRange}
        onChangeBeginDiffDate={model.onChangeBeginDiffDate}
        onChangeEndDiffDate={model.onChangeEndDiffDate}
        handleSetWithDiff={model.handleSetWithDiff}
        handleGetDiff={model.handleGetDiff}
        handleSetSaveFilterOpen={model.handleSetSaveFilterOpen}
        values={values}>
        <Schedule
          scheduleRef={scheduleRef}
          title={locale.schedule}
          isOpen={values.isScheduleOpen}
          onToggle={model.handleSetScheduleOpen}
          series={chartSeries}
          withDiff={values.withDiff}
          currency={currency}
          isMobile={isMobile}
        />
      </ReportFilter>

      <SavedFilters
        isFormOpen={values.isSaveFilterOpen}
        filterParams={model.savedFilterParams}
        onCloseForm={() => model.handleSetSaveFilterOpen(false)}
        onSave={model.handleSaveFilter}
        onApply={model.handleApplySavedFilter}
        onDelete={model.handleDeleteSavedFilter}
      />

      <ProvidersTable
        providers={statisticProvider.providers}
        values={values}
        handlers={{
          onProviderToggle: model.handleProviderToggle,
          onProviderMetricsApply: model.handleProviderMetricsApply,
          onProviderScheduleOpen: handleProviderScheduleOpen,
        }}
        withDiff={values.withDiff}
        currency={currency}
      />
    </div>
  );
};

export default General;
